import type { NumberFormatOptions } from 'next-intl'
import { useFormatter } from 'next-intl'
import { useCallback } from 'react'

import emDash from '~/components/emdash'

import type { FormatterInputScalar } from './types'

type NumberFormatterOptions = NumberFormatOptions & {
  export?: boolean
  invalidStr?: string
  // This doesn't do anything. It's here to keep the API consistent with the other formatters.
  omitUnits?: boolean
}

export const useNumberFormatter = () => {
  const format = useFormatter()

  return useCallback(
    (value: FormatterInputScalar, options?: NumberFormatterOptions) => {
      const { invalidStr, ...rest } = { invalidStr: options?.export ? '' : emDash, ...options }

      if (value === undefined || value === null) return invalidStr

      const valueToFormat = typeof value === 'string' ? parseFloat(value) : value
      if (isNaN(valueToFormat)) return invalidStr

      // For CSV export, disable digit grouping so the thousands separator doesn't collide with the
      // CSV field delimiter (e.g. en-US grouping uses ",", which is also the default delimiter).
      // The decimal separator still follows the user's locale; the field delimiter is chosen
      // accordingly elsewhere (see ~/data/utils/csv).
      return format.number(valueToFormat, { ...rest, useGrouping: !options?.export })
    },
    [format]
  )
}

/**
 * Formats a raw alarm register value as a fixed-width binary bitmap: MSB-first,
 * zero-padded to 16 bits and grouped into 4-bit nibbles
 * (e.g. `2052` -> `"0000 1000 0000 0100"`, `5` -> `"0000 0000 0000 0101"`).
 *
 * Risen alarm registers are 16-bit (UINT16), so 16 bits is the natural width;
 * any wider value is left untruncated but stays nibble-aligned from the LSB.
 */
export function formatRegisterBitmap(value: number): string {
  const bits = Math.trunc(value).toString(2).padStart(16, '0')
  return bits.replace(/\B(?=(\d{4})+(?!\d))/g, ' ')
}

/**
 * Rounds a number to the specified number of decimal places (default 1).
 */
export function roundNumber(value: string | number, places = 1, direction?: 'up' | 'down') {
  if (typeof value === 'string') {
    value = parseFloat(value)
  }

  const multiplier = Math.pow(10, Math.max(places, 0))

  // Number.EPSILON is used to avoid floating point rounding errors. For example:
  //   Math.round(1.005 * 100) / 100  -->  1
  //   Math.round((1.005 + Number.EPSILON) * 100) / 100  -->  1.01
  //
  // See more:
  //   https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/EPSILON
  //   https://stackoverflow.com/questions/11832914/how-to-round-to-at-most-2-decimal-places-if-necessary

  if (direction === 'up') {
    return Math.ceil((value + Number.EPSILON) * multiplier) / multiplier
  } else if (direction === 'down') {
    return Math.floor((value + Number.EPSILON) * multiplier) / multiplier
  }
  return Math.round((value + Number.EPSILON) * multiplier) / multiplier
}
